// The card of a role in the stack of modals (R-A3, R-A5, R-U27): its name, who wears it, what it reads and changes by section, hidden
// fields and the rights of actions. A working copy saved by «Сохранить», active only with changes; leaving with changes asks (B2).

import { useState } from 'react';
import type { Permission, RoleDef } from '@confdeck/core-shared';
import { useMeta, useSync } from '../sync/react.tsx';
import { useUnsaved } from '../shell/unsaved.ts';
import { StackPane } from '../ui/ModalStack.tsx';
import { Button, Checkbox, Chip, Input, Popover, PopoverContent, PopoverTrigger, Segmented, cx, useToast } from '../ui/primitives.tsx';
import { RolePeople } from './RolePeople.tsx';
import s from './team.module.css';

const PERM_LABEL: Record<'none' | Permission, string> = { none: 'Нет', read: 'Чтение', write: 'Изменение' };

/** What the card edits, in one comparable shape. */
const copyOf = (r: RoleDef) => ({ name: r.name, perms: r.perms, hidden: r.hidden ?? [], rights: r.rights ?? [] });
const canon = (c: ReturnType<typeof copyOf>) => JSON.stringify([c.name.trim(), Object.entries(c.perms).sort(), [...c.hidden].sort(), [...c.rights].sort()]);

export function RoleCard({ role, layer, onOpenPerson, onGrant, onClose }: {
  role: RoleDef; layer: string;
  onOpenPerson: (id: string) => void;
  /** Where the role is given: the people of the team. */
  onGrant: () => void;
  onClose: () => void;
}) {
  const meta = useMeta();
  const client = useSync();
  const toast = useToast();
  const [c, setC] = useState(() => copyOf(role));
  const catalog = client.header?.rights ?? [];
  const sections = client.menu.filter((m) => m.typeIds.length);
  const dirty = canon(c) !== canon(copyOf(role));
  const set = (typeId: string, p: 'none' | Permission) => { const perms = { ...c.perms }; if (p === 'none') delete perms[typeId]; else perms[typeId] = p; setC({ ...c, perms }); };
  const save = async (): Promise<boolean> => {
    if (!c.name.trim()) { toast({ kind: 'error', text: 'У роли должно быть название' }); return false; }
    const r = await client.run('role.save', { id: role.id, name: c.name.trim(), description: role.description, perms: c.perms, hidden: c.hidden, ...(role.full ? {} : { rights: c.rights }) });
    toast(r.ok ? { kind: 'info', text: 'Роль сохранена — права людей с ней уже изменились' } : { kind: 'error', text: r.error.message });
    return r.ok;
  };
  useUnsaved(dirty, save, `роль «${role.name}»`, layer);
  const archive = async () => {
    const r = await client.run('role.archive', { id: role.id });
    toast(r.ok ? { kind: 'info', text: 'Роль удалена' } : { kind: 'error', text: r.error.message });
    if (r.ok) onClose();
  };
  return (
    <StackPane onClose={onClose}
      title={<Input bare className={s.paneName} value={c.name} onChange={(e) => setC({ ...c, name: e.target.value })} aria-label="Название роли" />}
      sub={role.description || 'Права роли по разделам. Ограничение по событию задаётся, когда роль выдают человеку.'}
      footer={(
        <>
          {!role.base && <Button size="sm" variant="ghost" icon="trash" onClick={() => void archive()}>Удалить роль</Button>}
          {role.base && <Chip color="gray">Базовая роль: её можно настроить, но не удалить</Chip>}
          <span className={s.spacer} />
          {dirty && <Button size="sm" onClick={() => setC(copyOf(role))}>Отменить</Button>}
          <Button size="sm" variant="primary" disabled={!dirty} onClick={() => void save()}>Сохранить</Button>
        </>
      )}>
      <RolePeople role={role} onOpenPerson={onOpenPerson} onGrant={onGrant} />
      {sections.map((m) => (
        <div key={m.key} className={s.permGroup}>
          <div className={s.permHead}>{m.label}</div>
          {m.typeIds.map((tid) => {
            const cur = c.perms[tid] ?? 'none';
            return (
              <div key={tid} className={s.permRow}>
                <span>{meta.type(tid)?.nameMany}</span>
                <span className={s.permCtl}>
                  {cur !== 'none' && (
                    <Popover>
                      <PopoverTrigger asChild><Button size="sm" variant="ghost" caret>{(() => { const n = meta.fields(tid).filter((f) => c.hidden.includes(f.id)).length; return n ? `Скрыто полей: ${n}` : 'Поля'; })()}</Button></PopoverTrigger>
                      <PopoverContent width={300}>
                        <div className={s.fieldList}>
                          <div className={s.help}>Отмеченные поля роль видит; снятые не приходят в браузер.</div>
                          {meta.fields(tid).filter((f) => !f.archivedAt && f.key !== meta.type(tid)?.titleField).map((f) => (
                            <Checkbox key={f.id} checked={!c.hidden.includes(f.id)} onChange={(on) => setC({ ...c, hidden: on ? c.hidden.filter((x) => x !== f.id) : [...c.hidden, f.id] })}>{f.name}</Checkbox>
                          ))}
                        </div>
                      </PopoverContent>
                    </Popover>
                  )}
                  <Segmented size="sm" label={`Права: ${meta.type(tid)?.nameMany ?? ''}`} value={cur} onChange={(p) => set(tid, p)} items={(['none', 'read', 'write'] as const).map((p) => ({ value: p, label: PERM_LABEL[p] }))} className={s.perms} />
                </span>
              </div>
            );
          })}
        </div>
      ))}
      {!role.full && catalog.length > 0 && (
        <div className={s.permGroup}>
          <div className={s.permHead}>Действия</div>
          <p className={cx(s.help, s.groupHelp)}>Права на действия сверх изменения записей: регистратор правит заказы, но деньги возвращает тот, у кого есть это право.</p>
          {catalog.map((r) => (
            <label key={r.key} className={s.permRow}>
              <span>{r.name}<span className={s.rightHint}>{r.description}</span></span>
              <Checkbox label={r.name} checked={c.rights.includes(r.key)} onChange={(on) => setC({ ...c, rights: on ? [...c.rights, r.key] : c.rights.filter((x) => x !== r.key) })} />
            </label>
          ))}
        </div>
      )}
    </StackPane>
  );
}
